import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components/macro';
import { FormViews } from './enums/FormViews';
import FormsDrafting from './FormsDrafting';
import { FormsContext } from '../context/FormsContext';
import FormsPreview from './FormsPreview';
import ViewControl from './ViewControl';
import FloatingActionToolbar from './FloatingActionToolbar';
import useForms from '../hooks/useForms';
import EmptyForm from './EmptyForm';
import TemplateSelector from './TemplateSelector';
import { Box, Center, useDisclosure } from '@televet/kibble-ui/build/chakra';
import { Prompt, useHistory, useParams, useLocation } from 'react-router-dom';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import UnsavedFormChangesModal, { PageType } from '../components/UnsavedFormChangesModal';
import KeepNewFormModal from '../components/KeepNewFormModal';
import { Location } from 'history';

const Forms = (): JSX.Element => {
  const { formTemplateId } = useParams<{ formTemplateId: string }>();
  const [currentView, setCurrentView] = useState<FormViews>(FormViews.Compose);
  const { formId, isTemplateSelectorShown, isNewForm, hasChanges, dispatch } = useContext(FormsContext);
  const { getFormTemplates, templatesLoading, scrollToForm } = useForms();
  const {
    isOpen: isUnsavedChangesOpen,
    onOpen: onUnsavedChangesOpen,
    onClose: onUnsavedChangesClose,
  } = useDisclosure();
  const location = useLocation();
  const [lastLocation, setLastLocation] = useState<Location>(location);
  const history = useHistory();
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const [shouldUnload, setShouldUnload] = useState(false);
  const { isOpen: isKeepOpen, onOpen: onKeepOpen, onClose: onKeepClose } = useDisclosure();

  useEffect(() => {
    if (formId) scrollToForm(formId);
  }, [formId, scrollToForm]);

  const toggleView = (): void => {
    if (currentView === FormViews.Compose) {
      setCurrentView(FormViews.Preview);
    } else {
      setCurrentView(FormViews.Compose);
    }
  };

  const handleBlockedNavigation = (location: Location): boolean => {
    if (!confirmedNavigation && (hasChanges || isNewForm)) {
      setLastLocation(location);
      setShouldUnload(true);
      if (isNewForm && !hasChanges) onKeepOpen();
      if (hasChanges) onUnsavedChangesOpen();
      return false;
    }
    return true;
  };

  const onConfirm = (): void => {
    if (hasChanges) onUnsavedChangesClose();
    if (isNewForm) onKeepClose();
    setConfirmedNavigation(true);
    setShouldUnload(false);
    dispatch({
      type: 'RESET_NEW_FORM',
      payload: {},
    });
  };

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      history.push(lastLocation?.pathname);
      setConfirmedNavigation(false);
    }
  }, [confirmedNavigation, lastLocation, history]);

  useEffect(() => {
    const unload = (event: BeforeUnloadEvent): void => {
      if ((hasChanges || isNewForm) && !shouldUnload) {
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
        event.returnValue = ''; // deprecated-ish
        event.preventDefault(); // not fully supported yet
      }
    };

    window.addEventListener('beforeunload', unload);

    return (): void => window.removeEventListener('beforeunload', unload);
  }, [hasChanges, shouldUnload, isNewForm]);

  return isTemplateSelectorShown ? (
    <TemplateSelector />
  ) : (
    <>
      <Center flex="1 1 auto" flexDirection="column" bgColor="background.default">
        {formTemplateId && formTemplateId !== formId && isNewForm ? (
          <LoadingSpinner message="Creating form..." size="xl" />
        ) : (
          <>
            {formTemplateId && formId ? (
              <>
                <ViewControl currentView={currentView} toggleView={toggleView} />
                <FormsContentContainer>
                  <Box mt="70px" mx="auto" px={5} maxW="600px">
                    {currentView === FormViews.Compose && <FormsDrafting />}
                    {currentView === FormViews.Preview && <FormsPreview />}
                  </Box>
                </FormsContentContainer>
                <FloatingActionToolbar getFormTemplates={getFormTemplates} templatesLoading={templatesLoading} />
              </>
            ) : (
              <EmptyForm />
            )}
          </>
        )}
      </Center>
      <Prompt when={hasChanges || isNewForm} message={handleBlockedNavigation} />
      <UnsavedFormChangesModal
        isOpen={isUnsavedChangesOpen}
        onClose={onUnsavedChangesClose}
        onConfirm={onConfirm}
        pageType={PageType.Form}
      />
      <KeepNewFormModal isOpen={isKeepOpen} onClose={onKeepClose} formTemplateId={formId} onConfirm={onConfirm} />
    </>
  );
};

export default Forms;

const FormsContentContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  scroll-behavior: smooth;

  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 0;
    width: 7px;
  }

  &::-webkit-scrollbar-track {
    margin: 5px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.25);
    border-radius: 10px;
    box-shadow: rgba(255, 255, 255, 0.3) 0 0 0 1px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.35);
  }
`;
